"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { getStrapiMedia } from "@/lib/strapi/client";
import type { TeaserVideoBlockType } from "@/lib/types/teaser";

type Props = {
  data: TeaserVideoBlockType;
};

function youtubeVideoId(url: string): string | null {
  try {
    const parsed = new URL(url.startsWith("http") ? url : `https://${url}`);
    const host = parsed.hostname.replace(/^www\./, "");

    if (host === "youtu.be") {
      return parsed.pathname.replace(/^\//, "").split("/")[0] || null;
    }

    if (host === "youtube.com" || host === "m.youtube.com") {
      const id = parsed.searchParams.get("v");
      if (id) return id;
      return parsed.pathname.match(/\/embed\/([^/]+)/)?.[1] ?? null;
    }
  } catch {
    return null;
  }
  return null;
}

function vimeoVideoId(url: string): string | null {
  try {
    const parsed = new URL(url.startsWith("http") ? url : `https://${url}`);
    if (parsed.hostname.replace(/^www\./, "") !== "vimeo.com") return null;
    return parsed.pathname.split("/").filter(Boolean)[0] || null;
  } catch {
    return null;
  }
}

function toEmbedUrl(raw: string): string {
  const url = raw.trim();
  if (!url) return url;

  try {
    const parsed = new URL(url.startsWith("http") ? url : `https://${url}`);
    const host = parsed.hostname.replace(/^www\./, "");

    if (host === "youtu.be") {
      const id = parsed.pathname.replace(/^\//, "");
      return id ? `https://www.youtube.com/embed/${id}?autoplay=1&rel=0` : url;
    }

    if (host === "youtube.com" || host === "m.youtube.com") {
      const id = parsed.searchParams.get("v");
      if (id) return `https://www.youtube.com/embed/${id}?autoplay=1&rel=0`;
      const embed = parsed.pathname.match(/\/embed\/([^/]+)/)?.[1];
      if (embed)
        return `https://www.youtube.com/embed/${embed}?autoplay=1&rel=0`;
    }

    if (host === "vimeo.com") {
      const id = parsed.pathname.split("/").filter(Boolean)[0];
      if (id) return `https://player.vimeo.com/video/${id}?autoplay=1`;
    }
  } catch {
    return url;
  }

  return url;
}

function youtubePoster(raw: string): string | null {
  const id = youtubeVideoId(raw);
  return id ? `https://i.ytimg.com/vi/${id}/hqdefault.jpg` : null;
}

function PlayIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 23 27"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M4.27734 0.413535C3.41016 -0.119668 2.32031 -0.137246 1.43555 0.360801C0.550781 0.858848 0 1.79635 0 2.81588V23.4409C0 24.4604 0.550781 25.3979 1.43555 25.896C2.32031 26.394 3.41016 26.3706 4.27734 25.8432L21.1523 15.5307C21.9902 15.021 22.5 14.1128 22.5 13.1284C22.5 12.144 21.9902 11.2417 21.1523 10.726L4.27734 0.413535Z"
        fill="#FF8C18"
      />
    </svg>
  );
}

export default function TeaserVideo({ data }: Props) {
  if (data.isVisible === false) return null;

  const externalRaw = data.isExternalVideo ? data.videoUrl?.trim() : null;
  const mediaUrl = !data.isExternalVideo
    ? getStrapiMedia(data.video?.url ?? null)
    : null;
  const thumbnailUrl = getStrapiMedia(data.thumbnail?.url ?? null) || null;

  if (!externalRaw && !mediaUrl) return null;

  return (
    <TeaserVideoPlayer
      externalUrl={externalRaw ?? null}
      mediaUrl={mediaUrl ?? null}
      thumbnailUrl={thumbnailUrl}
      title={data.video?.name ?? "Video"}
    />
  );
}

function TeaserVideoPlayer({
  externalUrl,
  mediaUrl,
  thumbnailUrl,
  title,
}: {
  externalUrl: string | null;
  mediaUrl: string | null;
  thumbnailUrl: string | null;
  title: string;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const primingFrameRef = useRef(false);
  const [playing, setPlaying] = useState(false);
  const [externalPoster, setExternalPoster] = useState<string | null>(() => {
    if (thumbnailUrl) return thumbnailUrl;
    return externalUrl ? youtubePoster(externalUrl) : null;
  });

  // Paint the first frame of an uploaded video when CMS has no thumbnail.
  // Mobile Safari often ignores seek alone — muted play()/pause() forces a decode.
  const paintFirstFrame = useCallback(() => {
    const video = videoRef.current;
    if (!video || !mediaUrl || playing || thumbnailUrl) return;

    const paint = async () => {
      try {
        if (video.currentTime < 0.05) video.currentTime = 0.1;
      } catch {
        /* ignore */
      }

      primingFrameRef.current = true;
      try {
        video.muted = true;
        await video.play();
        video.pause();
        if (video.currentTime < 0.05) video.currentTime = 0.1;
      } catch {
        /* autoplay blocked — seek may still show a frame on some browsers */
      } finally {
        primingFrameRef.current = false;
      }
    };

    if (video.readyState >= 2) void paint();
    else
      video.addEventListener("loadeddata", () => void paint(), { once: true });
  }, [mediaUrl, playing, thumbnailUrl]);

  useEffect(() => {
    paintFirstFrame();
  }, [paintFirstFrame]);

  const uploadedSrc =
    mediaUrl && !mediaUrl.includes("#") ? `${mediaUrl}#t=0.1` : mediaUrl;

  // Vimeo poster via oEmbed when no CMS / YouTube poster.
  useEffect(() => {
    if (!externalUrl || playing || thumbnailUrl || youtubePoster(externalUrl))
      return;
    const id = vimeoVideoId(externalUrl);
    if (!id) return;

    let cancelled = false;
    fetch(
      `https://vimeo.com/api/oembed.json?url=${encodeURIComponent(`https://vimeo.com/${id}`)}`,
    )
      .then((res) => (res.ok ? res.json() : null))
      .then((json: { thumbnail_url?: string } | null) => {
        if (!cancelled && json?.thumbnail_url)
          setExternalPoster(json.thumbnail_url);
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [externalUrl, playing, thumbnailUrl]);

  const start = useCallback(() => {
    setPlaying(true);
    const video = videoRef.current;
    if (mediaUrl && video) {
      void video.play().catch(() => {});
    }
  }, [mediaUrl]);

  const embedSrc = externalUrl ? toEmbedUrl(externalUrl) : null;
  const uploadedPoster = thumbnailUrl;

  return (
    <section
      data-fig="teaser-video"
      className="relative h-[300px] w-full overflow-hidden bg-[#3C3C3C] md:h-[660px]"
      aria-label="Video"
    >
      {externalUrl ? (
        playing && embedSrc ? (
          <iframe
            src={embedSrc}
            title={title}
            className="absolute inset-0 h-full w-full border-0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        ) : externalPoster ? (
          // eslint-disable-next-line @next/next/no-img-element -- remote poster URL
          <img
            src={externalPoster}
            alt=""
            className="absolute inset-0 h-full w-full object-cover"
          />
        ) : (
          <div className="absolute inset-0 bg-[#3C3C3C]" aria-hidden="true" />
        )
      ) : (
        <>
          {uploadedPoster && !playing ? (
            // eslint-disable-next-line @next/next/no-img-element -- CMS poster URL
            <img
              src={uploadedPoster}
              alt=""
              className="absolute inset-0 h-full w-full object-cover"
            />
          ) : null}
          <video
            ref={videoRef}
            className={`absolute inset-0 h-full w-full object-cover ${
              uploadedPoster && !playing ? "opacity-0" : ""
            }`}
            src={uploadedSrc ?? undefined}
            poster={uploadedPoster ?? undefined}
            playsInline
            preload="metadata"
            controls={playing}
            muted
            onLoadedMetadata={paintFirstFrame}
            onLoadedData={paintFirstFrame}
            onPlay={() => {
              if (!primingFrameRef.current) setPlaying(true);
            }}
          />
        </>
      )}

      {!playing && (
        <button
          type="button"
          onClick={start}
          className="absolute left-1/2 top-1/2 z-10 isolate flex size-14 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 pl-[23.1875px] pr-[19.6875px] shadow-[0px_8.75px_13.125px_-2.625px_rgba(0,0,0,0.1),0px_3.5px_5.25px_-3.5px_rgba(0,0,0,0.1)] backdrop-blur-[1.75px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FF8C18] focus-visible:ring-offset-2 md:size-20 md:pl-[33.125px] md:pr-[28.125px] md:shadow-[0px_12.5px_18.75px_-3.75px_rgba(0,0,0,0.1),0px_5px_7.5px_-5px_rgba(0,0,0,0.1)] md:backdrop-blur-[2.5px]"
          aria-label="Play video"
        >
          <PlayIcon className="relative z-[1] h-[21px] w-[15.75px] shrink-0 md:h-[30px] md:w-[22.5px]" />
        </button>
      )}
    </section>
  );
}
